import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { hotProducts } from "@/mocks/products";

interface HotProductsProps {
  onAddToCart: () => void;
}

const tagColors: Record<string, string> = {
  爆款: "bg-red-500",
  新品: "bg-green-500",
  热销: "bg-orange-500",
  礼盒: "bg-pink-500",
  限量: "bg-rose-600",
  专业: "bg-slate-600",
  奢品: "bg-amber-600",
};

export default function HotProducts({ onAddToCart }: HotProductsProps) {
  const [likedIds, setLikedIds] = useState<number[]>([]);
  const [activeFilter, setActiveFilter] = useState("全部");
  const navigate = useNavigate();

  const filters = ["全部", "手机数码", "家用电器", "美妆护肤", "服装鞋包", "运动户外", "家居家具", "母婴玩具", "奢侈品"];

  const filtered = activeFilter === "全部"
    ? hotProducts
    : hotProducts.filter((p) => p.category === activeFilter);

  const toggleLike = (id: number) => {
    setLikedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  return (
    <section className="py-8 md:py-10 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-1 h-6 md:h-7 bg-red-500 rounded-full"></div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900" style={{ fontFamily: "'Noto Serif SC', serif" }}>
              热门商品
            </h2>
            <span className="bg-red-100 text-red-500 text-xs px-2 py-0.5 rounded-full font-medium hidden sm:inline-flex">
              <i className="ri-fire-line mr-0.5"></i> 今日爆款
            </span>
          </div>
          <button className="text-red-500 text-sm hover:underline cursor-pointer whitespace-nowrap">
            查看更多 <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>

        {/* Filters - horizontally scrollable on mobile */}
        <div className="flex items-center gap-2 mb-4 md:mb-6 overflow-x-auto pb-1 scrollbar-hide">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              className={`px-3 md:px-4 py-1.5 rounded-full text-xs md:text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                activeFilter === f
                  ? "bg-red-500 text-white"
                  : "bg-white text-gray-500 hover:text-red-500 border border-gray-200"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Product grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {filtered.map((product) => (
            <div
              key={product.id}
              onClick={() => navigate(`/product/${product.id}`)}
              className="bg-white rounded-xl overflow-hidden cursor-pointer group hover:scale-[1.02] transition-transform duration-200"
            >
              <div className="relative overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-36 md:h-48 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-2 left-2">
                  <span className={`${tagColors[product.tag] || "bg-gray-500"} text-white text-xs px-1.5 py-0.5 rounded-full font-medium`}>
                    {product.tag}
                  </span>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); toggleLike(product.id); }}
                  className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-white/80 rounded-full cursor-pointer hover:bg-white transition-colors"
                >
                  <i className={`text-sm ${likedIds.includes(product.id) ? "ri-heart-fill text-red-500" : "ri-heart-line text-gray-400"}`}></i>
                </button>
              </div>
              <div className="p-2.5 md:p-3">
                <p className="text-gray-800 text-xs md:text-sm font-medium leading-snug line-clamp-2 mb-1.5 md:mb-2">
                  {product.name}
                </p>
                <div className="flex items-center gap-0.5 mb-1.5 md:mb-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i
                      key={i}
                      className={`text-xs ${i < Math.floor(product.rating) ? "ri-star-fill text-amber-400" : "ri-star-line text-gray-300"}`}
                    ></i>
                  ))}
                  <span className="text-xs text-gray-400 ml-1 hidden sm:inline">{product.rating}</span>
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <span className="text-red-500 font-bold text-base md:text-lg">¥{product.price.toLocaleString()}</span>
                    <span className="text-gray-400 text-xs line-through ml-1 hidden sm:inline">¥{product.originalPrice.toLocaleString()}</span>
                  </div>
                  <span className="text-gray-400 text-xs hidden sm:block">{product.sales.toLocaleString()}人购买</span>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); onAddToCart(); }}
                  className="mt-2 md:mt-3 w-full bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-shopping-cart-line mr-1"></i> 加入购物车
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
